// Generic, source-agnostic Board view (ADR D3/P0.5).
//
// Each bucket from the ViewState engine becomes a column; records render as
// RecordCards. Defaults to grouping by status when the caller's ViewState says
// "none" (a board needs columns). Cards can be dragged between columns to
// change whatever field the board is currently grouped by (#876 D) — the drop
// maps the target column to a normalized change and applies it. Groupings
// with no well-defined "set this field" semantic (due/project/list) leave DnD
// disabled — see DND_CAPABLE_GROUPINGS.

import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import type { DragStart, DropResult } from "@hello-pangea/dnd";
import { TASK_PRIORITIES } from "@juuplan/shared";
import type { TaskPriority } from "@juuplan/shared";
import { RecordCard } from "../components/RecordCard";
import { batchUpdateTasks, moveTasksToPhase } from "../lib/api";
import {
  beginListDrop,
  endListDrop,
  isListDropCandidate,
  moveTaskToList,
} from "../store/listDrop";
import type { ResolverContext, TaskRecord, TaskRecordChange } from "../lib/taskRecord";
import { applyFilters, applySort, bucket, type BucketOptions, type GroupBy, type ViewState } from "../lib/viewState";

/**
 * Groupings whose bucket key maps to a settable field, i.e. dropping a record
 * into a column is a meaningful action. Shared with TaskListView so both
 * surfaces agree on exactly which groupings offer drag affordance — where a
 * grouping isn't listed here, DnD stays off rather than being silently inert.
 */
export const DND_CAPABLE_GROUPINGS: ReadonlySet<GroupBy> = new Set([
  "status",
  "priority",
  "assignee",
  "type",
  "phase",
]);

interface Props {
  records: TaskRecord[];
  state: ViewState;
  ctx: ResolverContext;
  bucketOpts?: BucketOptions;
  readOnly?: boolean;
  onOpenRecord?: (record: TaskRecord) => void;
  emptyLabel?: string;
}

/**
 * Map a status-board column (identified by its bucket key) to the change that
 * moving a record into it should apply. Returns null when the drop is a no-op
 * or the column can't be expressed as a status change (non-status groupings,
 * the "Sonstiges" fallback column).
 *
 * For category columns (cross-source boards, e.g. "Meine Aufgaben"), we first
 * try to resolve the record's own status set to a concrete status whose
 * category matches the target column — that lets a project task move into
 * "In Arbeit". Falls back to the done/open toggle when no such status exists
 * (personal tasks, or a project without a status in that category).
 */
export function columnChange(
  colKey: string,
  record: TaskRecord,
  ctx: ResolverContext
): TaskRecordChange | null {
  if (colKey.startsWith("status:")) {
    const rest = colKey.slice("status:".length);
    if (rest === "_fallback") return null;
    const id = Number(rest);
    if (!Number.isFinite(id) || record.status.id === id) return null;
    return { statusId: id };
  }
  if (colKey.startsWith("cat:")) {
    const cat = colKey.slice("cat:".length);
    const match = ctx.resolveStatuses?.(record)?.find((s) => s.category === cat);
    if (match) {
      if (record.status.id === match.id) return null;
      return { statusId: match.id };
    }
    const targetDone = cat === "done" || cat === "cancelled";
    if (record.done === targetDone) return null;
    return { done: targetDone };
  }
  if (colKey.startsWith("prio:")) {
    const rest = colKey.slice("prio:".length);
    if (rest !== "none" && !TASK_PRIORITIES.includes(rest as TaskPriority)) return null; // malformed key
    const value: TaskPriority | null = rest === "none" ? null : (rest as TaskPriority);
    if ((record.priority ?? null) === value) return null;
    return { priority: value };
  }
  if (colKey.startsWith("user:")) {
    const rest = colKey.slice("user:".length);
    if (rest === "none") {
      if (record.assigneeIds.length === 0) return null;
      return { assigneeIds: [] };
    }
    const id = Number(rest);
    if (!Number.isFinite(id)) return null;
    // Dropping onto a user column makes them primary (assignee_ids[0], see
    // taskRecord.ts/api docs) while keeping the record's other assignees.
    if (record.primaryAssigneeId === id) return null;
    return { assigneeIds: [id, ...record.assigneeIds.filter((a) => a !== id)] };
  }
  // "phase:<id>"/"type:<id>" columns are handled by resolvePhaseMove/
  // resolveTypeMove below, NOT here — TaskRecordChange (taskRecord.ts, out of
  // scope to extend) has no phaseId/typeId field, so neither can be expressed
  // as a ctx.patch change. Both mutate through a dedicated endpoint instead.
  return null;
}

/**
 * Decide whether dropping `record` onto a phase-board column is a valid
 * cross-phase move, returning the target phase id or null.
 *
 * Phase is deliberately NOT wired through `columnChange`/`ctx.patch`: a
 * task's phase is not a `TaskUpdate` field (`TaskCreateSchema`/`TaskUpdateSchema`
 * have no `phase_id`), so `PATCH /api/tasks/:id` cannot move a task between
 * phases. Cross-phase moves go only through `PATCH /api/tasks/:id/reorder`
 * (`moveTasksToPhase`, api.ts), which also shifts sibling sort_order — callers
 * must invoke that directly instead of routing through this module's
 * `columnChange`/`ctx.patch` pair.
 *
 * Returns null (no-op) for: non-phase columns, the record's own phase, the
 * "Ohne Phase" fallback column (not a settable target), personal-task records
 * (no phase concept), and sub-tasks (the server rejects cross-phase moves for
 * them — they must follow their parent's phase).
 */
export function resolvePhaseMove(colKey: string, record: TaskRecord): number | null {
  if (!colKey.startsWith("phase:")) return null;
  if (record.source !== "project") return null;
  if (record.parentId !== null) return null;
  const rest = colKey.slice("phase:".length);
  if (rest === "none") return null;
  const id = Number(rest);
  if (!Number.isFinite(id) || record.phaseId === id) return null;
  return id;
}

/**
 * Decide whether dropping `record` onto a type-board column is a valid type
 * change, returning the target work-item-type id or null.
 *
 * Like phase, `type_id` is absent from `TaskRecordChange` (taskRecord.ts is
 * out of scope to extend), so it can't be expressed via `ctx.patch` either.
 * Unlike phase there's no single-task convenience endpoint, so callers apply
 * the result via `batchUpdateTasks({ ids: [record.id], patch: { type_id } })`.
 *
 * Returns null for: non-type columns, the record's current type, personal
 * tasks (no work-item types), and the "Ohne Typ" column — `type_id` is
 * `.optional()` but not `.nullable()` on `TaskUpdateSchema`, so there is no
 * way to clear it via this endpoint.
 */
export function resolveTypeMove(colKey: string, record: TaskRecord): number | null {
  if (!colKey.startsWith("type:")) return null;
  if (record.source !== "project") return null;
  const rest = colKey.slice("type:".length);
  if (rest === "none") return null;
  const id = Number(rest);
  if (!Number.isFinite(id) || (record.typeId ?? null) === id) return null;
  return id;
}

export function TaskBoardView(props: Props): preact.JSX.Element {
  const { records, state, ctx, bucketOpts, readOnly = false, emptyLabel = "Keine Aufgaben" } = props;
  if (records.length === 0) {
    return (
      <p class="rounded border border-dashed border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-4 py-8 text-center text-sm text-[color:var(--color-on-surface-variant)] italic">
        {emptyLabel}
      </p>
    );
  }

  // A board must have columns — fall back to status grouping when caller said "none".
  const groupBy: GroupBy = state.groupBy === "none" ? "status" : state.groupBy;
  const filtered = applyFilters(records, state.filters, { today: bucketOpts?.today });
  const sorted = applySort(filtered, state.sort);
  const columns = bucket(sorted, groupBy, bucketOpts);
  const showProject = groupBy !== "project";
  const showStatus = groupBy !== "status";
  // Column drops are only offered on groupings with a well-defined "set this
  // field" semantic (#876 D) — everywhere else (due/project/list) they stay off
  // rather than being a silently-inert affordance. Cards stay draggable anyway
  // when a personal task could be dropped onto a sidebar list (store/listDrop),
  // which works independently of the grouping.
  const listDroppable = records.some(isListDropCandidate);
  const dndEnabled = !readOnly && (DND_CAPABLE_GROUPINGS.has(groupBy) || listDroppable);
  const colDroppable = DND_CAPABLE_GROUPINGS.has(groupBy);
  /** Per-card gate — same reasoning as TaskListView's rowDraggable. */
  const cardDraggable = (r: TaskRecord): boolean =>
    dndEnabled && (colDroppable || isListDropCandidate(r));

  // uid → record lookup so onDragEnd can resolve the dragged card. Built over
  // the sorted (post-filter) list, which is exactly what the columns render.
  const byUid = new Map<string, TaskRecord>();
  for (const r of sorted) byUid.set(r.uid, r);

  // @hello-pangea/dnd is touch-capable (unlike native HTML5 drag), so drag to
  // change status works on phones too. The board only moves cards between
  // columns (no in-column reorder) — same-column drops are a no-op.
  const onDragStart = (start: DragStart): void => {
    const rec = byUid.get(start.draggableId);
    if (rec && isListDropCandidate(rec)) beginListDrop(rec.listId);
  };

  const onDragEnd = (result: DropResult): void => {
    const { source, destination, draggableId } = result;
    // Always ends the pointer tracking, also for drags that never started it.
    const listTarget = endListDrop();
    const rec = byUid.get(draggableId);
    // Same precedence rule as TaskListView: a sidebar list under the pointer
    // wins over the column destination hello-pangea derived from the card's
    // centre, and a cancelled drag never moves anything.
    if (result.reason === "DROP" && listTarget !== null && rec && isListDropCandidate(rec)) {
      void moveTaskToList(rec.id, listTarget);
      return;
    }
    if (!destination) return;
    const destKey = destination.droppableId.slice("board-col-".length);
    const srcKey = source.droppableId.slice("board-col-".length);
    if (destKey === srcKey) return;
    // isDragDisabled already blocks sub-task drags at the gesture level; this
    // is defense in depth against a stale draggableId.
    if (!rec || rec.parentId !== null) return;
    if (destKey.startsWith("phase:")) {
      // Phase can't be expressed as a TaskRecordChange — route through the
      // dedicated reorder endpoint instead of ctx.patch (see resolvePhaseMove).
      const phaseId = resolvePhaseMove(destKey, rec);
      if (phaseId !== null) void moveTasksToPhase([rec.id], phaseId);
      return;
    }
    if (destKey.startsWith("type:")) {
      // Same reasoning as phase — type_id has no TaskRecordChange field.
      const typeId = resolveTypeMove(destKey, rec);
      if (typeId !== null) void batchUpdateTasks({ ids: [rec.id], patch: { type_id: typeId } });
      return;
    }
    const change = columnChange(destKey, rec, ctx);
    if (change) void ctx.patch(rec, change);
  };

  return (
    <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div class="flex gap-3 overflow-x-auto snap-x snap-mandatory md:snap-none pb-2 -mx-1 px-1">
        {columns.map((col) => (
          <div
            key={col.key}
            class="shrink-0 w-64 snap-start flex flex-col rounded-lg bg-[color:var(--color-surface-container-low)]"
          >
            <div
              class="flex items-center gap-2 px-3 py-2 rounded-t-lg border-b-2"
              style={{ borderColor: col.color }}
            >
              <span class="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: col.color }} aria-hidden="true" />
              <span class="text-xs font-semibold text-[color:var(--color-on-surface)] truncate">{col.label || "Alle"}</span>
              <span class="ml-auto text-label text-[color:var(--color-on-surface-variant)] tabular-nums">{col.items.length}</span>
            </div>
            <Droppable droppableId={`board-col-${col.key}`} type="BOARD_CARD" isDropDisabled={!dndEnabled}>
              {(prov, snap) => (
                <div
                  ref={prov.innerRef}
                  {...prov.droppableProps}
                  class={`flex flex-col gap-2 p-2 min-h-[40px] flex-1 transition-colors ${
                    snap.isDraggingOver
                      ? "ring-2 ring-inset ring-[color:var(--color-primary)] bg-[color:var(--color-surface-container)]"
                      : ""
                  }`}
                >
                  {col.items.length === 0 ? (
                    <p class="px-1 py-2 text-label text-[color:var(--color-on-surface-variant)] italic">
                      {snap.isDraggingOver ? "Hier ablegen" : "Leer"}
                    </p>
                  ) : (
                    col.items.map((r, idx) => (
                      <Draggable
                        key={r.uid}
                        draggableId={r.uid}
                        index={idx}
                        // Sub-tasks stay non-draggable, consistent with TableView
                        // (they must follow their parent's phase/grouping).
                        isDragDisabled={!cardDraggable(r) || r.parentId !== null}
                      >
                        {(dragProv, dragSnap) => {
                          const baseStyle =
                            (dragProv.draggableProps.style as preact.JSX.CSSProperties | undefined) ?? {};
                          return (
                            <RecordCard
                              record={r}
                              ctx={ctx}
                              readOnly={readOnly}
                              showProject={showProject}
                              showStatus={showStatus}
                              onOpen={props.onOpenRecord}
                              innerRef={dragProv.innerRef as (el: HTMLElement | null) => void}
                              draggableProps={dragProv.draggableProps as unknown as Record<string, unknown>}
                              dragHandleProps={
                                (dragProv.dragHandleProps ?? undefined) as Record<string, unknown> | undefined
                              }
                              isDragging={dragSnap.isDragging}
                              style={baseStyle}
                            />
                          );
                        }}
                      </Draggable>
                    ))
                  )}
                  {prov.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        ))}
      </div>
    </DragDropContext>
  );
}
